const TZ = "Europe/Dublin";

/** Offset of Europe/Dublin from UTC in ms at a given instant. */
function offsetAt(ts: number): number {
  const d = new Date(ts);
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  const representedAsUtc = Date.UTC(
    get("year"),
    get("month") - 1,
    get("day"),
    get("hour") % 24,
    get("minute"),
    get("second"),
  );
  return representedAsUtc - d.getTime();
}

/**
 * Convert a wall-clock date ("YYYY-MM-DD") and time ("HH:mm") entered by the
 * user into the exact UTC instant for that moment in Dublin, regardless of the
 * device's own timezone.
 */
export function dublinWallTimeToDate(date: string, time: string): Date {
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  const naive = Date.UTC(y, (m ?? 1) - 1, d ?? 1, hh ?? 0, mm ?? 0, 0, 0);
  let ts = naive - offsetAt(naive);
  ts = naive - offsetAt(ts);
  return new Date(ts);
}

export function dublinWallTimeToIso(date: string, time: string): string {
  return dublinWallTimeToDate(date, time).toISOString();
}

/** Same, but for a datetime-local input value ("YYYY-MM-DDTHH:mm"). */
export function dublinLocalInputToIso(value: string): string {
  const [date, time = "00:00"] = value.split("T");
  return dublinWallTimeToIso(date, time.slice(0, 5));
}

/** UTC boundaries for one Dublin calendar day, including DST transition days. */
export function dublinDayBoundsIso(date: string): { from: string; to: string } {
  const [year, month, day] = date.split("-").map(Number);
  const next = new Date(Date.UTC(year, (month ?? 1) - 1, day ?? 1, 12));
  next.setUTCDate(next.getUTCDate() + 1);
  const nextDate = next.toISOString().slice(0, 10);
  return {
    from: dublinWallTimeToIso(date, "00:00"),
    to: dublinWallTimeToIso(nextDate, "00:00"),
  };
}

/** ISO timestamp formatted for a datetime-local field in Dublin wall time. */
export function isoToDublinLocalInput(iso: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "00";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
}

/** Current Dublin wall clock as a datetime-local input value. */
export function nowDublinInputValue(offsetMs = 0): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(Date.now() + offsetMs));
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "00";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
}
